import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { SupabaseService } from '../supabase/supabase';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { IPrompts } from '../../shared/models';

// Riga app_prompt: id (UUID v7, time-ordered) + oggetto IPrompts completo.
export interface IPromptVersionRow {
  id: string;
  prompts: IPrompts;
  created_at: string;
}

// Storico append-only dei prompt su Supabase. "Ultimo" = id (UUID v7, time-ordered) più recente.
// Il "rollback" (Specifica_Sviluppatori_FaseZero §4, Versionamento) è un re-insert della versione
// scelta come nuova riga: lo storico resta intatto e "ultima versione" torna a essere quella.
@Injectable({ providedIn: 'root' })
export class PromptService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);

  // --- public ---
  // Ultima versione dei prompt CON il suo id (per il version stamping sugli esiti Fase Zero);
  // null se Supabase non configurato o tabella vuota.
  public getLatestPrompts(): Observable<{ id: string; prompts: IPrompts } | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<{ id: string; prompts: IPrompts } | null>(
      client.from('app_prompt').select('id, prompts').order('id', { ascending: false }).limit(1).maybeSingle(),
    ).pipe(map((row) => row ?? null));
  }

  // Storico versioni (id + data), dalla più recente: per la lista "Ripristina versione" in /admin.
  public listVersions(limit = 20): Observable<{ id: string; created_at: string }[]> {
    const client = this._supabase.client;
    if (null === client) {
      return of([]);
    }
    return doSupabaseCall<{ id: string; created_at: string }[]>(
      client.from('app_prompt').select('id, created_at').order('id', { ascending: false }).limit(limit),
    ).pipe(map((rows) => rows ?? []));
  }

  public getPromptsById(id: string): Observable<IPrompts | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<{ prompts: IPrompts } | null>(
      client.from('app_prompt').select('prompts').eq('id', id).maybeSingle(),
    ).pipe(map((row) => row?.prompts ?? null));
  }

  // Salva una nuova versione (append-only). Nessuna password: accesso gestito dalla route.
  public savePrompts(prompts: IPrompts): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(client.from('app_prompt').insert({ prompts })).pipe(map(() => undefined));
  }
}
